/**
 * GovOppExtras — Requirement Extractor (materials Shopping List) and
 * Perplexity Deep Research (procurement contacts) sections used inside the
 * PublicWorks opportunity DetailModal.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Package, UserSearch, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REL_COLORS = {
  high: "bg-emerald-100 text-emerald-700 border-emerald-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};

export function MaterialsSection({ opp, isPt }) {
  const [loading, setLoading] = useState(false);
  const [req, setReq] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  useEffect(() => { setReq(opp?.requirements || null); setPdfUrl(""); }, [opp?.id]);
  if (!opp) return null;

  const extract = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/public-works/opportunities/${opp.id}/extract-requirements`,
        { lang: isPt ? "pt" : "en", pdf_url: pdfUrl.trim() });
      setReq(data.requirements);
      toast.success(isPt ? "Materiais extraídos" : "Requirements extracted");
    } catch (_e) {
      toast.error(isPt ? "Falha na extração" : "Extraction failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#F0FDF4] border border-emerald-200 rounded-lg p-4" data-testid="pwf-materials-section">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-emerald-700 font-bold inline-flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" /> {isPt ? "Shopping List de Materiais" : "Materials Shopping List"}
          </h4>
          <p className="text-[11.5px] text-[#6B7280] mt-0.5">
            {isPt ? "Extrai Concrete, Steel, Lumber, HVAC... do edital (texto + PDF)" : "Extracts Concrete, Steel, Lumber, HVAC... from the solicitation"}
          </p>
        </div>
        <button type="button" data-testid="pwf-extract-btn" onClick={extract} disabled={loading}
          className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Package className="w-3.5 h-3.5" />}
          {req ? (isPt ? "Re-extrair" : "Re-extract") : (isPt ? "Extrair materiais" : "Extract requirements")}
        </button>
      </div>
      <input type="url" value={pdfUrl} onChange={(e) => setPdfUrl(e.target.value)}
        placeholder={isPt ? "URL do PDF do edital (opcional)" : "Solicitation PDF URL (optional)"}
        data-testid="pwf-extract-pdf-url"
        className="w-full h-8 px-2.5 rounded-md border border-emerald-200 text-[12px] mb-2 bg-white" />
      {req ? (
        <div className="space-y-2.5 text-[12.5px]">
          {req.summary_for_supplier && <p className="italic">{req.summary_for_supplier}</p>}
          {(req.shopping_list || []).length === 0 && (
            <p className="text-[#6B7280]">{isPt ? "Nenhum material identificado." : "No materials identified."}</p>
          )}
          {(req.shopping_list || []).map((s, i) => (
            <div key={`sl-${s.category || i}`} className="bg-white border border-emerald-100 rounded p-2.5">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-[12.5px]">{s.category}</span>
                <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded border ${REL_COLORS[s.relevance] || REL_COLORS.low}`}>
                  {(s.relevance || "low").toUpperCase()}
                </span>
                {s.quantity_hint && <span className="text-[11px] text-[#6B7280]">{s.quantity_hint}</span>}
              </div>
              <div className="flex flex-wrap gap-1">
                {(s.items || []).map((it, j) => (
                  <span key={`it-${j}`} className="text-[10.5px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-100">{it}</span>
                ))}
              </div>
            </div>
          ))}
          {(req.sub_contracting || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase font-bold text-[#6B7280] mb-1">{isPt ? "Subcontratação" : "Sub-contracting"}</div>
              <ul className="list-disc list-inside space-y-0.5">{req.sub_contracting.map((s, i) => <li key={`sc-${i}`}>{s}</li>)}</ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-[12px] text-[#6B7280] italic">
          {isPt ? "Clique em 'Extrair materiais' para gerar a shopping list." : "Click 'Extract requirements' to build the shopping list."}
        </p>
      )}
    </div>
  );
}

export function ResearchSection({ opp, isPt }) {
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  useEffect(() => { setRes(opp?.research_contacts || null); }, [opp?.id]);
  if (!opp) return null;

  const research = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/public-works/opportunities/${opp.id}/research-contacts`,
        { lang: isPt ? "pt" : "en" });
      setRes(data.research);
      toast.success(isPt ? "Pesquisa concluída" : "Research done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isPt ? "Falha na pesquisa" : "Research failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#F5F3FF] border border-violet-200 rounded-lg p-4" data-testid="pwf-research-section">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <h4 className="text-[11px] uppercase tracking-wide text-violet-700 font-bold inline-flex items-center gap-1.5">
            <UserSearch className="w-3.5 h-3.5" /> {isPt ? "Deep Research de Contatos" : "Contacts Deep Research"}
          </h4>
          <p className="text-[11.5px] text-[#6B7280] mt-0.5">
            {isPt ? "Perplexity busca os procurement officers / PMs deste contrato" : "Perplexity finds the procurement officers / PMs for this contract"}
          </p>
        </div>
        <button type="button" data-testid="pwf-research-btn" onClick={research} disabled={loading}
          className="h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserSearch className="w-3.5 h-3.5" />}
          {loading ? (isPt ? "Pesquisando..." : "Researching...") : res ? (isPt ? "Re-pesquisar" : "Re-research") : (isPt ? "Pesquisar contatos" : "Research contacts")}
        </button>
      </div>
      {res ? (
        <div className="text-[12.5px] space-y-2">
          <div className="whitespace-pre-wrap bg-white border border-violet-100 rounded p-3 max-h-64 overflow-y-auto" data-testid="pwf-research-answer">
            {res.answer}
          </div>
          {(res.citations || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {res.citations.slice(0, 6).map((c, i) => (
                <a key={`cit-${i}`} href={c} target="_blank" rel="noopener noreferrer"
                  className="text-[10.5px] px-1.5 py-0.5 rounded bg-white border border-violet-100 text-violet-700 inline-flex items-center gap-1 hover:underline">
                  <ExternalLink className="w-2.5 h-2.5" /> {`[${i + 1}]`}
                </a>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-[12px] text-[#6B7280] italic">
          {isPt ? "Identifique nomes, cargos e e-mails dos responsáveis pela licitação." : "Identify names, titles and emails of the people behind this solicitation."}
        </p>
      )}
    </div>
  );
}
